import { useEffect, useMemo, useState } from "react";
import useBackendUrl from "@/hooks/useBackendUrl";

const TIERS = [
  { threshold: 0, status: "kein_status", discount: 0 },
  { threshold: 5, status: "bronze", discount: 5 },
  { threshold: 25, status: "silber", discount: 10 },
  { threshold: 50, status: "gold", discount: 20 },
  { threshold: 100, status: "platin", discount: 30 },
];

const STATUS_ALIASES = {
  "no-status": "kein_status",
};

let cachedActiveProgram = null;
let cachedError = null;
let cachedAt = 0;
let inflightRequest = null;

const CACHE_TTL_MS = 30_000;

const sameProgram = (a, b) =>
  (a?.id || null) === (b?.id || null) &&
  JSON.stringify(a?.start || null) === JSON.stringify(b?.start || null) &&
  JSON.stringify(a?.end || null) === JSON.stringify(b?.end || null) &&
  (a?.status || null) === (b?.status || null);

async function fetchActiveProgram(api) {
  const now = Date.now();
  if (now - cachedAt < CACHE_TTL_MS) {
    if (cachedError) throw cachedError;
    return cachedActiveProgram;
  }

  if (!inflightRequest) {
    inflightRequest = fetch(`${api}/auction/treueprogramm/current`, {
      method: "GET",
      credentials: "include",
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            data?.error || "Treueprogramm konnte nicht geladen werden.",
          );
        }
        cachedActiveProgram = data?.activeProgram || null;
        cachedError = null;
        cachedAt = Date.now();
        return cachedActiveProgram;
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          cachedActiveProgram = null;
          cachedError = err;
          cachedAt = Date.now();
        }
        throw err;
      })
      .finally(() => {
        inflightRequest = null;
      });
  }

  return inflightRequest;
}

export default function useTPStatus(userData) {
  const api = useBackendUrl();
  const [activeProgram, setActiveProgram] = useState(cachedActiveProgram);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(cachedError);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setLoading((prev) => (prev ? prev : true));
        setError((prev) => (prev === null ? prev : null));
        const nextActiveProgram = await fetchActiveProgram(api);
        if (ac.signal.aborted) return;
        setActiveProgram((prev) => {
          if (sameProgram(prev, nextActiveProgram)) return prev;
          return nextActiveProgram;
        });
      } catch (err) {
        if (err?.name === "AbortError") return;
        setActiveProgram((prev) => (prev === null ? prev : null));
        setError((prev) => (prev === err ? prev : err));
      } finally {
        if (!ac.signal.aborted) {
          setLoading((prev) => (prev ? false : prev));
        }
      }
    })();

    return () => ac.abort();
  }, [api]);

  const currentTreueprogramm = userData?.currentTreueprogramm || null;
  const isActiveProgram =
    !!activeProgram?.id &&
    currentTreueprogramm?.programId === activeProgram.id;
  const count = isActiveProgram
    ? Number(currentTreueprogramm?.vehicleCount) || 0
    : 0;
  const savedAmount = isActiveProgram
    ? Number(currentTreueprogramm?.discount) || 0
    : 0;
  const storedStatus =
    (isActiveProgram &&
      (STATUS_ALIASES[currentTreueprogramm?.status] ||
        currentTreueprogramm?.status)) ||
    null;

  const { status, discount, nextStatus, vehiclesToNext } = useMemo(() => {
    const tier =
      TIERS.find((item) => item.status === storedStatus) ||
      [...TIERS].reverse().find((item) => count >= item.threshold) ||
      TIERS[0];
    const next = TIERS[TIERS.indexOf(tier) + 1] || null;

    return {
      status: tier.status,
      discount: tier.discount,
      nextStatus: next?.status ?? null,
      vehiclesToNext: next ? Math.max(0, next.threshold - count) : 0,
    };
  }, [count, storedStatus]);

  return {
    count,
    status,
    discount,
    savedAmount,
    activeProgram,
    isActiveProgram,
    programStart: activeProgram?.start,
    programEnd: activeProgram?.end,
    nextStatus,
    vehiclesToNext,
    loading,
    error,
  };
}
